import InputField from '@/components/forms/InputField';
import PhoneField from '@/components/forms/PhoneField';
import SelectField from '@/components/forms/SelectField';
import RequestDemoFieldset from '@/components/RequestDemo/RequestDemoFieldset';
import RequestDemoFormElement from '@/components/RequestDemo/RequestDemoFormElement';
import { GTM_VARIABLES } from '@/constants/gtm';
import {
  HubspotCompanySizeOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs,
  HubspotIndustryDropdownOptions,
  HubspotPrimaryUsecaseDropdownOptions
} from '@/constants/hubspot';
import { OPTIMIZELY_EVENTS } from '@/constants/optimizely';
import { UserContext } from '@/context/user';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import gtm from '@/lib/gtm';
import { checkHubspotEmailDomain } from '@/lib/hubspot';
import optimizely from '@/lib/optimizely';
import { FC, useContext, useEffect, useRef } from 'react';
import { PrimaryInnerGlowButton } from '../buttons/primary';
import { DemoFormStepOneFields } from './DemoFormStepOne';

export type DemoFormStepTwoFields = {
  [HubspotFieldNames.CompanyName]: string;
  [HubspotFieldNames.CompanySize]: string;
  [HubspotFieldNames.Email]: string;
  [HubspotFieldNames.FirstName]: string;
  [HubspotFieldNames.Industry]: string;
  [HubspotFieldNames.JobTitle]: string;
  [HubspotFieldNames.LastName]: string;
  [HubspotFieldNames.PrimaryUsecase]: string;
  [HubspotFieldNames.Phone]: string;
  [HubspotFieldNames.MarketingEmailOptOut]: boolean;
  /*
   * Hidden fields for tracking Qualified meeting status - values should only be sent if the meeting is not offered
   * If a meeting WAS offered, the values are sent to HS via workflows in Qualified
   */
  [HubspotFieldNames.QualifiedBookingStatus]?: string;
  [HubspotFieldNames.MostRecentQualifiedMeetingOfferedStatus]?: string;
  [HubspotFieldNames.MostRecentQualifiedBookingStatus]?: string;
  [HubspotFieldNames.QualifiedMeetingOffered]?: string;
  [HubspotFieldNames.QualifiedMeetingNotOfferedReason]?: string;
}

// Default values for when a meeting is not offered
const DEFAULT_MEETING_NOT_OFFERED_VALUES = {
  [HubspotFieldNames.QualifiedMeetingOffered]: 'No',
  [HubspotFieldNames.MostRecentQualifiedMeetingOfferedStatus]: 'No',
  [HubspotFieldNames.QualifiedBookingStatus]: 'Was not shown calendar',
  [HubspotFieldNames.MostRecentQualifiedBookingStatus]: 'Was not shown calendar',
}

// Values for when a meeting is not offered because the company size is below the threshold
const BELOW_COMPANY_SIZE_THRESHOLD_VALUES = {
  ...DEFAULT_MEETING_NOT_OFFERED_VALUES,
  [HubspotFieldNames.QualifiedMeetingNotOfferedReason]: 'Company size below threshold',
}

// Values for when a meeting is not offered because the user is an existing customer
const EXISTING_CUSTOMER_VALUES = {
  ...DEFAULT_MEETING_NOT_OFFERED_VALUES,
  [HubspotFieldNames.QualifiedMeetingNotOfferedReason]: 'Flagged as existing customer',
}

export type FormStepProps = {
  initialValues: DemoFormStepOneFields;
  onDemoFormTwoSuccess: (values: DemoFormStepTwoFields, meetingOffered: boolean) => void;
}

const DemoFormStepTwo: FC<FormStepProps> = ({ initialValues, onDemoFormTwoSuccess }) => {
  const { getTempUserData } = useContext(UserContext);

  // Using a ref instead of state because we don't need a re-render when the value changes
  const meetingOffered = useRef<boolean>(true);

  const formik = useCustomFormik<DemoFormStepTwoFields>({
    initialValues: {
      [HubspotFieldNames.CompanySize]: initialValues[HubspotFieldNames.CompanySize],
      [HubspotFieldNames.Email]: initialValues[HubspotFieldNames.Email],
      [HubspotFieldNames.CompanyName]: '',
      [HubspotFieldNames.FirstName]: '',
      [HubspotFieldNames.Industry]: '',
      [HubspotFieldNames.JobTitle]: '',
      [HubspotFieldNames.LastName]: '',
      [HubspotFieldNames.PrimaryUsecase]: '',
      [HubspotFieldNames.Phone]: '',
      // Marketing email opt in - the user must now actively opt in, so in GDPR regions the initial value is opted out. 
      [HubspotFieldNames.MarketingEmailOptOut]: initialValues[HubspotFieldNames.MarketingEmailOptOut],
    },
    onSubmit: useFormikHubspotSubmit({
      formId: HubspotFormIDs.RequestDemo,
      name: 'Request Demo Modal',
      content: 'Request Demo Modal',
      beforeSubmit: async (values) => {
        const { isPayingCustomer } = await checkHubspotEmailDomain(values[HubspotFieldNames.Email]);

        // If already a customer, do not offer a meeting and show generic confirmation
        if (isPayingCustomer) {
          Object.assign(values, EXISTING_CUSTOMER_VALUES);
          meetingOffered.current = false;

          // If company size is below threshold, do not offer a meeting and show generic confirmation
        } else if (parseInt(values[HubspotFieldNames.CompanySize]) < parseInt(HubspotCompanySizeOptions.Small)) {
          Object.assign(values, BELOW_COMPANY_SIZE_THRESHOLD_VALUES);
          meetingOffered.current = false;
        }
      },
      onSuccess: async (values) => {
        gtm.trackDemoRequest({
          [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
          [GTM_VARIABLES.USER_FIRST_NAME]: values[HubspotFieldNames.FirstName],
          [GTM_VARIABLES.USER_LAST_NAME]: values[HubspotFieldNames.LastName],
          [GTM_VARIABLES.USER_PHONE]: values[HubspotFieldNames.Phone],
        });

        optimizely.event(OPTIMIZELY_EVENTS.ANY_GET_STARTED_MODAL_FORM_SUBMIT);
        optimizely.event(OPTIMIZELY_EVENTS.REQUEST_DEMO_SUCCESS);

        onDemoFormTwoSuccess(values, meetingOffered.current);
      },
      onError: () => {
        //
      },
    }),
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Industry] = HubspotFieldValidators[HubspotFieldNames.Industry](
        values[HubspotFieldNames.Industry],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
        values[HubspotFieldNames.JobTitle],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Phone] = HubspotFieldValidators[HubspotFieldNames.Phone](
        values[HubspotFieldNames.Phone],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.PrimaryUsecase] = HubspotFieldValidators[HubspotFieldNames.PrimaryUsecase](
        values[HubspotFieldNames.PrimaryUsecase],
        {
          required: true,
        },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  useEffect(() => {
    const data = getTempUserData();

    Object.keys(data).forEach((key) => {
      formik.setFieldValue(key, data[key]);
    });
  }, []);

  return (
    <RequestDemoFormElement onSubmit={formik.handleSubmit} noValidate>
      <RequestDemoFieldset>
        <InputField
          type="text"
          name={HubspotFieldNames.FirstName}
          label={HubspotFieldLabels.FirstName}
          placeholder="Jamie"
          value={formik.values[HubspotFieldNames.FirstName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[HubspotFieldNames.FirstName] && !!formik.errors[HubspotFieldNames.FirstName]}
          errorMessage={formik.errors[HubspotFieldNames.FirstName] as string}
          translucent
        />

        <InputField
          type="text"
          name={HubspotFieldNames.LastName}
          label={HubspotFieldLabels.LastName}
          placeholder="Smith"
          value={formik.values[HubspotFieldNames.LastName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[HubspotFieldNames.LastName] && !!formik.errors[HubspotFieldNames.LastName]}
          errorMessage={formik.errors[HubspotFieldNames.LastName] as string}
          translucent
        />
      </RequestDemoFieldset>

      <RequestDemoFieldset>
        <InputField
          type="text"
          name={HubspotFieldNames.CompanyName}
          label={HubspotFieldLabels.CompanyName}
          placeholder="Your Company"
          value={formik.values[HubspotFieldNames.CompanyName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[HubspotFieldNames.CompanyName] && !!formik.errors[HubspotFieldNames.CompanyName]}
          errorMessage={formik.errors[HubspotFieldNames.CompanyName] as string}
          translucent
        />

        <InputField
          type="text"
          name={HubspotFieldNames.JobTitle}
          label={HubspotFieldLabels.JobTitle}
          placeholder="e.g., Director of Operations"
          value={formik.values[HubspotFieldNames.JobTitle]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[HubspotFieldNames.JobTitle] && !!formik.errors[HubspotFieldNames.JobTitle]}
          errorMessage={formik.errors[HubspotFieldNames.JobTitle] as string}
          translucent
        />
      </RequestDemoFieldset>

      <RequestDemoFieldset>
        <SelectField
          name={HubspotFieldNames.Industry}
          label={HubspotFieldLabels.Industry}
          options={HubspotIndustryDropdownOptions}
          value={formik.values[HubspotFieldNames.Industry]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[HubspotFieldNames.Industry] && !!formik.errors[HubspotFieldNames.Industry]}
          errorMessage={formik.errors[HubspotFieldNames.Industry] as string}
          translucent
        />

        <PhoneField
          name={HubspotFieldNames.Phone}
          label={HubspotFieldLabels.Phone}
          value={formik.values[HubspotFieldNames.Phone]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[HubspotFieldNames.Phone] && !!formik.errors[HubspotFieldNames.Phone]}
          errorMessage={formik.errors[HubspotFieldNames.Phone] as string}
        />
      </RequestDemoFieldset>

      <SelectField
        name={HubspotFieldNames.PrimaryUsecase}
        label={HubspotFieldLabels.PrimaryUsecase}
        options={HubspotPrimaryUsecaseDropdownOptions}
        value={formik.values[HubspotFieldNames.PrimaryUsecase]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        invalid={formik.touched[HubspotFieldNames.PrimaryUsecase] && !!formik.errors[HubspotFieldNames.PrimaryUsecase]}
        errorMessage={formik.errors[HubspotFieldNames.PrimaryUsecase] as string}
        translucent
      />

      <div className="flex justify-end pt-4">
        <PrimaryInnerGlowButton type="submit" disabled={formik.isValidating || formik.isSubmitting}>
          {formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Submit'}
        </PrimaryInnerGlowButton>
      </div>
    </RequestDemoFormElement>
  );
};

export default DemoFormStepTwo;
